import type {PageHandle} from "#webgl2/webgl2-image-atlas"
import {WebGl2ImageAtlas} from "#webgl2/webgl2-image-atlas"
import {WebGl2ImagePhysical} from "#webgl2/webgl2-image-physical"
import type {Box2Like, Vector2Like} from "@cm/math"
import {Box2, Size2, Vector2} from "@cm/math"
import {WebGl2ImageVirtualMipmapInfo} from "#webgl2/webgl2-image-virtual-mipmap-info"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import type {WebGl2ImageDescriptor} from "#webgl2/webgl2-image"
import type {HalImageDescriptor, PageDescriptor} from "@cm/hal"
import {getNumChannels} from "@cm/hal"
import {WebGl2ImageVirtualPageTableUpdates} from "#webgl2/webgl2-image-virtual-page-table-updates"
import {Subject} from "rxjs"
import {loggerFactory} from "@cm/browser-log"

export class WebGl2ImageVirtualPageManager {
    readonly pageAdded = new Subject<PageDescriptor>()
    readonly pageRemoved = new Subject<PageDescriptor>()
    readonly beforePageRemoved = new Subject<PageDescriptor>()

    constructor(
        readonly descriptor: HalImageDescriptor,
        readonly context: WebGl2Context,
    ) {
        const numLayers = descriptor.numLayers ?? 1
        if (numLayers !== 1) {
            throw Error("Number of layers must be 1.")
        }
        if (descriptor.width < 0 || descriptor.height < 0) {
            throw Error("Image dimensions must be positive.")
        }
        if (!Number.isInteger(descriptor.width) || !Number.isInteger(descriptor.height)) {
            throw Error("Image dimensions must be integers.")
        }

        const start = performance.now()

        this.mipmapInfo = new WebGl2ImageVirtualMipmapInfo(descriptor)
        const numChannels = getNumChannels(descriptor.channelLayout)

        logger.trace(
            `Creating virtual WebGL texture with of size ${descriptor.width}x${descriptor.height} containing ${this.mipmapInfo.getNumMipLevels()} mipmap levels.`,
        )

        this.pageTableImage = new WebGl2ImagePhysical(this.context, {
            ...this.mipmapInfo.pageLookupSize,
            channelLayout: "R",
            dataType: "float32",
            numMipLevels: 1,
        })

        const end = performance.now()
        logger.trace("Created virtual GPU image in " + (end - start) + "ms")

        this.webGl2ImageDescriptor = {
            width: descriptor.width,
            height: descriptor.height,
            channelLayout: descriptor.channelLayout,
            dataType: descriptor.dataType,
            textureOffset: new Vector2(0, 0),
            wrapOffset: new Vector2(0, 0),
            wrapSize: new Size2(descriptor.width, descriptor.height),
            wrapGap: new Size2(0, 0),
            mipLevelOffset: 0,
            shardSize: this.pageTableImage.descriptor.shardSize,
            numShards: this.pageTableImage.descriptor.numShards,
            numLayers: numChannels,
            numMipLevels: this.mipmapInfo.getNumMipLevels(),
        }

        this._pageTableUpdates = new WebGl2ImageVirtualPageTableUpdates(this.context, this.mipmapInfo, numChannels)
    }

    dispose() {
        this._pageTableUpdates.dispose()
        this.pageTableImage.dispose()
    }

    setPagePriority(pagePriority: number): void {
        if (this._pagePriority === pagePriority) {
            return
        }
        this._pagePriority = pagePriority
        this._pageHandleInfoByLinearPageIndex.forEach((pageHandleInfo) =>
            pageHandleInfo.pageHandleByChannel.forEach((pageHandle) =>
                this.context.getAtlasById(pageHandle.atlasId).setPagePriority(pageHandle, this._pagePriority),
            ),
        )
    }

    getPageDescriptor(args: {pageIndex: Vector2Like; mipLevel: number}): PageDescriptor {
        const pageSize = this.context.context.descriptor.atlasPageSize
        // const maxPagesPerDim = 10000 // arbitrary limit used to compute hash
        // if (args.pageIndex.x < 0 || args.pageIndex.x >= maxPagesPerDim || args.pageIndex.y < 0 || args.pageIndex.y >= maxPagesPerDim) {
        //     throw new Error(`Invalid page index: ${args.pageIndex.x}, ${args.pageIndex.y}. Must be between 0 and ${maxPagesPerDim - 1}.`)
        // }
        // const hash = (args.mipLevel * maxPagesPerDim + args.pageIndex.y) * maxPagesPerDim + args.pageIndex.x
        const hash = (args.mipLevel * this.mipmapInfo.numBaseLevelPages.y + args.pageIndex.y) * this.mipmapInfo.numBaseLevelPages.x + args.pageIndex.x
        return {
            pageIndex: {
                x: args.pageIndex.x,
                y: args.pageIndex.y,
            },
            mipLevel: args.mipLevel,
            region: {
                x: args.pageIndex.x * pageSize,
                y: args.pageIndex.y * pageSize,
                width: pageSize,
                height: pageSize,
            },
            hash,
        }
    }

    // private computePageHash(args: {pageIndex: Vector2Like; mipLevel: number}): number {
    //     return (args.mipLevel * this.mipmapInfo.pageLookupSize.height + args.pageIndex.y) * this.mipmapInfo.pageLookupSize.width + args.pageIndex.x
    // }

    private updatePageDescriptor(pageHandleInfo: PageHandleInfo) {
        // update pageHandle in pageLookupTexture
        this.updatePageTable(pageHandleInfo.linearPageIndex, pageHandleInfo.pageHandleByChannel)
        this._pageHandleInfoByLinearPageIndex.set(pageHandleInfo.linearPageIndex, pageHandleInfo)
    }

    private updatePageTable(linearPageIndex: number, pageHandleByChannel: PageHandle[] | null) {
        logger.trace("updatePageTable", linearPageIndex, pageHandleByChannel)
        this._pageTableUpdates.add(linearPageIndex, pageHandleByChannel)
    }

    determinePages(region: Box2Like, mipLevel: number): PageDescriptor[] {
        const mipLevelSize = this.mipmapInfo.getMipLevelSize(mipLevel)
        region = Box2.intersect(region, {x: 0, y: 0, width: mipLevelSize.width, height: mipLevelSize.height})
        const pageSize = WebGl2ImageAtlas.pageSize
        const p00 = new Vector2(region.x, region.y).mul(1 / pageSize).floor()
        const p11 = new Vector2(region.x + region.width, region.y + region.height).mul(1 / pageSize).ceil()
        const regionPages = {
            x: p00.x,
            y: p00.y,
            width: p11.x - p00.x,
            height: p11.y - p00.y,
        }
        const pageDescriptors: PageDescriptor[] = []
        for (let y = 0; y < regionPages.height; y++) {
            for (let x = 0; x < regionPages.width; x++) {
                const pageDescriptor = this.getPageDescriptor({
                    pageIndex: {
                        x: regionPages.x + x,
                        y: regionPages.y + y,
                    },
                    mipLevel,
                })
                pageDescriptors.push(pageDescriptor)
            }
        }
        return pageDescriptors
    }

    regionHasSharedPages(region: Box2Like, mipLevel: number): boolean {
        const pageDescriptors = this.determinePages(region, mipLevel)
        return pageDescriptors.some((pageDescriptor) => (this.getAllocatedPageHandleForPageDescriptor(pageDescriptor)?.sharedHandle.totalRefCount ?? 0) > 1)
    }

    flushPageTableUpdates() {
        this.context.updatePageTable(this.pageTableImage, this._pageTableUpdates)
    }

    getOrAllocUniquePage(pageDescriptor: PageDescriptor): AllocatedUniquePageInfo {
        const linearPageIndex = this.mipmapInfo.getLinearPageIndex(pageDescriptor)
        let pageHandleInfo = this._pageHandleInfoByLinearPageIndex.get(linearPageIndex)
        let locksToTransfer = 0
        let sharedSourcePageHandleInfo: PageHandleInfo | undefined = undefined
        if (pageHandleInfo && pageHandleInfo.sharedHandle.totalRefCount > 1) {
            // this page is shared; create a unique page instead
            locksToTransfer = pageHandleInfo.sharedHandle.lockCountByHolder.get(this) ?? 0 // check if the shared page was locked; if so we need to transfer the lock to the new unique page
            this.unlockPagesInternal([pageHandleInfo.linearPageIndex], undefined) // release all locks from this page
            this.releaseSharedHandleReference(pageHandleInfo.sharedHandle)
            pageHandleInfo.associatedPageDescriptorsByPageHash.delete(pageDescriptor.hash)
            this._pageHandleInfoByLinearPageIndex.delete(linearPageIndex)
            sharedSourcePageHandleInfo = pageHandleInfo
            pageHandleInfo = undefined
        }
        if (!pageHandleInfo) {
            // this page is still missing
            const sharedHandle: SharedHandle = {
                totalRefCount: 1,
                refCountByHolder: new Map([[this, 1]]),
                totalLockCount: 0,
                lockCountByHolder: new Map(),
            }
            const pageHandleByChannel: PageHandle[] = []
            const newPageHandleInfo: PageHandleInfo = {
                linearPageIndex,
                pageHandleByChannel,
                sharedHandle,
                associatedPageDescriptorsByPageHash: new Map<number, PageDescriptor>(),
            }
            pageHandleInfo = newPageHandleInfo
            // Find (or create) an atlas with enough pages for all channels
            const atlas = this.context.findAtlasForAllocation(this.webGl2ImageDescriptor.numLayers)
            for (let i = 0; i < this.webGl2ImageDescriptor.numLayers; i++) {
                const pageHandle = atlas.addPage(this._pagePriority, (pageHandle) => {
                    for (const holder of sharedHandle.refCountByHolder.keys()) {
                        holder.onPageRemoved(newPageHandleInfo, pageHandle)
                    }
                })
                atlas.lockPage(pageHandle) // lock to avoid that the atlas frees this page while we are still setting up the others
                pageHandleByChannel.push(pageHandle)
            }
            pageHandleByChannel.forEach((pageHandle) => atlas.unlockPage(pageHandle)) // release setup lock
            this.updatePageDescriptor(pageHandleInfo)
            this.addDeferredPageLocks(pageHandleInfo.linearPageIndex)
            if (locksToTransfer > 0) {
                this.lockPagesInternal([pageHandleInfo.linearPageIndex], locksToTransfer)
            }
            this.pageAdded.next(pageDescriptor)
        } else {
            // this page has been requested before; update its usage
            pageHandleInfo.pageHandleByChannel.forEach((pageHandle) => this.context.getAtlasById(pageHandle.atlasId).registerPageUsage(pageHandle))
        }
        pageHandleInfo.associatedPageDescriptorsByPageHash.set(pageDescriptor.hash, pageDescriptor)
        return {pageHandleInfo, sharedSourcePageHandleInfo}
    }

    private onPageRemoved(pageHandleInfo: PageHandleInfo, _pageHandle: PageHandle): void {
        if (this._isFreeingPagesInternally) {
            return
        }
        logger.trace("onPageRemoved", pageHandleInfo)
        // make sure to remove all associated pageHandles
        this.freePages([pageHandleInfo.linearPageIndex], true)
    }

    sharePage(targetPageDescriptor: PageDescriptor, sourcePageDescriptor: PageHandleInfo) {
        const linearPageIndex = this.mipmapInfo.getLinearPageIndex(targetPageDescriptor)
        const existingTargetPageDescriptor = this._pageHandleInfoByLinearPageIndex.get(linearPageIndex)
        if (existingTargetPageDescriptor && existingTargetPageDescriptor.sharedHandle === sourcePageDescriptor.sharedHandle) {
            // this page is already shared
            existingTargetPageDescriptor.associatedPageDescriptorsByPageHash.set(targetPageDescriptor.hash, targetPageDescriptor)
            return
        }
        this.addSharedHandleReference(sourcePageDescriptor.sharedHandle)
        const pageHandleInfo: PageHandleInfo = {
            linearPageIndex,
            pageHandleByChannel: sourcePageDescriptor.pageHandleByChannel,
            sharedHandle: sourcePageDescriptor.sharedHandle,
            associatedPageDescriptorsByPageHash: new Map<number, PageDescriptor>(),
        }
        if (existingTargetPageDescriptor) {
            // this page is already allocated
            const numLocks = existingTargetPageDescriptor.sharedHandle.lockCountByHolder.get(this) ?? 0
            if (numLocks > 0) {
                this.unlockPagesInternal([pageHandleInfo.linearPageIndex], numLocks)
            }
            this.freePages([pageHandleInfo.linearPageIndex], false)
            this.updatePageDescriptor(pageHandleInfo)
            if (numLocks > 0) {
                this.lockPagesInternal([pageHandleInfo.linearPageIndex], numLocks)
            }
        } else {
            this.updatePageDescriptor(pageHandleInfo)
            this.pageAdded.next(targetPageDescriptor)
        }
        this.addDeferredPageLocks(pageHandleInfo.linearPageIndex)
        pageHandleInfo.associatedPageDescriptorsByPageHash.set(targetPageDescriptor.hash, targetPageDescriptor)
    }

    lockPages(pageDescriptors: readonly PageDescriptor[]): void {
        this.lockPagesInternal(
            pageDescriptors.map((pageDescriptor) => this.mipmapInfo.getLinearPageIndex(pageDescriptor)),
            1,
        )
    }

    unlockPages(pageDescriptors: readonly PageDescriptor[]): void {
        this.unlockPagesInternal(
            pageDescriptors.map((pageDescriptor) => this.mipmapInfo.getLinearPageIndex(pageDescriptor)),
            1,
        )
    }

    private addDeferredPageLocks(linearPageIndex: number) {
        const deferredPageLockCount = this._deferredPageLockCountByLinearPageIndex.get(linearPageIndex)
        if (deferredPageLockCount !== undefined) {
            this._deferredPageLockCountByLinearPageIndex.delete(linearPageIndex)
            this.lockPagesInternal([linearPageIndex], deferredPageLockCount)
        }
    }

    getLockCount(pageDescriptor: PageDescriptor): number {
        const linearPageIndex = this.mipmapInfo.getLinearPageIndex(pageDescriptor)
        let lockCount = this._deferredPageLockCountByLinearPageIndex.get(linearPageIndex) ?? 0
        const pageHandleInfo = this._pageHandleInfoByLinearPageIndex.get(linearPageIndex)
        if (pageHandleInfo) {
            lockCount += pageHandleInfo.sharedHandle.lockCountByHolder.get(this) ?? 0
        }
        return lockCount
    }

    freePages(linearPageIndices: readonly number[], notify: boolean): void {
        this._isFreeingPagesInternally = true
        linearPageIndices.forEach((linearPageIndex) => {
            const pageHandleInfo = this._pageHandleInfoByLinearPageIndex.get(linearPageIndex)
            if (!pageHandleInfo) {
                throw new Error("Internal error: pageHandleInfo is not allocated.")
            }
            const lockCount = pageHandleInfo.sharedHandle.lockCountByHolder.get(this)
            if (lockCount !== undefined && lockCount > 0) {
                throw new Error("Internal error: Attempting to free a locked page.")
            }
            if (notify) {
                pageHandleInfo.associatedPageDescriptorsByPageHash.forEach((pageDescriptor) => this.beforePageRemoved.next(pageDescriptor))
            }
            this.releaseSharedHandleReference(pageHandleInfo.sharedHandle)
            this._pageHandleInfoByLinearPageIndex.delete(linearPageIndex)
            // update pageHandle in pageLookupTexture
            this.updatePageTable(linearPageIndex, null)
            // actually free the atlas page if it is not shared
            if (pageHandleInfo.sharedHandle.refCountByHolder.size === 0) {
                pageHandleInfo.pageHandleByChannel.forEach((pageHandle) => this.context.getAtlasById(pageHandle.atlasId).freePage(pageHandle))
            }
            // notify listeners
            if (notify) {
                pageHandleInfo.associatedPageDescriptorsByPageHash.forEach((pageDescriptor) => this.pageRemoved.next(pageDescriptor))
            }
        })
        this._isFreeingPagesInternally = false
    }

    private addSharedHandleReference(sharedHandle: SharedHandle) {
        let refCount = sharedHandle.refCountByHolder.get(this) ?? 0
        refCount++
        sharedHandle.refCountByHolder.set(this, refCount)
        sharedHandle.totalRefCount++
        return refCount
    }

    private releaseSharedHandleReference(sharedHandle: SharedHandle) {
        // adjust ref count
        let refCount = sharedHandle.refCountByHolder.get(this)
        if (refCount === undefined) {
            throw new Error("Internal error: this page is not shared with this image.")
        }
        refCount--
        if (refCount < 0) {
            throw new Error("Internal error: shared ref count is negative.")
        }
        if (refCount === 0) {
            sharedHandle.refCountByHolder.delete(this)
        } else {
            sharedHandle.refCountByHolder.set(this, refCount)
        }
        sharedHandle.totalRefCount--
        if (sharedHandle.totalRefCount < 0) {
            throw new Error("Internal error: shared ref count is negative.")
        }
        return refCount
    }

    private lockPagesInternal(linearPageIndices: readonly number[], numLocks: number): void {
        if (numLocks <= 0) {
            throw new Error("Invalid number of locks.")
        }
        linearPageIndices.forEach((linearPageIndex) => {
            const webGl2PageDescriptor = this._pageHandleInfoByLinearPageIndex.get(linearPageIndex)
            if (webGl2PageDescriptor) {
                if (webGl2PageDescriptor.sharedHandle.totalLockCount === 0) {
                    webGl2PageDescriptor.pageHandleByChannel.forEach((pageHandle) => this.context.getAtlasById(pageHandle.atlasId).lockPage(pageHandle))
                }
                let thisLockCount = webGl2PageDescriptor.sharedHandle.lockCountByHolder.get(this) ?? 0
                thisLockCount += numLocks
                webGl2PageDescriptor.sharedHandle.lockCountByHolder.set(this, thisLockCount)
                webGl2PageDescriptor.sharedHandle.totalLockCount += numLocks
            } else {
                this._deferredPageLockCountByLinearPageIndex.set(
                    linearPageIndex,
                    (this._deferredPageLockCountByLinearPageIndex.get(linearPageIndex) ?? 0) + numLocks,
                )
            }
        })
    }

    private unlockPagesInternal(linearPageIndices: readonly number[], numLocks: number | undefined): void {
        if (numLocks !== undefined && numLocks <= 0) {
            throw new Error("Invalid number of locks.")
        }
        linearPageIndices.forEach((linearPageIndex) => {
            const webGl2PageDescriptor = this._pageHandleInfoByLinearPageIndex.get(linearPageIndex)
            if (!webGl2PageDescriptor) {
                // no page descriptor ? maybe it is still a deferred lock ?
                const deferredLockCount = this._deferredPageLockCountByLinearPageIndex.get(linearPageIndex)
                if (deferredLockCount !== undefined) {
                    if (numLocks !== undefined && numLocks > deferredLockCount) {
                        throw new Error("Attempting to unlock more locks than held (deferred).")
                    }
                    const remainingLocks = deferredLockCount - (numLocks ?? deferredLockCount)
                    if (remainingLocks > 0) {
                        this._deferredPageLockCountByLinearPageIndex.set(linearPageIndex, remainingLocks)
                    } else {
                        this._deferredPageLockCountByLinearPageIndex.delete(linearPageIndex)
                    }
                    return
                }
                throw new Error("Attempting to unlock a page that is not allocated.")
            }
            if (this._deferredPageLockCountByLinearPageIndex.has(linearPageIndex)) {
                throw new Error("Internal error: Unexpected deferred page lock count during unlock.")
            }
            let thisLockCount = webGl2PageDescriptor.sharedHandle.lockCountByHolder.get(this) ?? 0
            const locksToRelease = numLocks ?? thisLockCount
            if (locksToRelease > 0) {
                if (thisLockCount < locksToRelease) {
                    throw new Error("Internal error: Not enough locks held by this image.")
                }
                thisLockCount -= locksToRelease
                if (thisLockCount > 0) {
                    webGl2PageDescriptor.sharedHandle.lockCountByHolder.set(this, thisLockCount)
                } else {
                    webGl2PageDescriptor.sharedHandle.lockCountByHolder.delete(this)
                }
                webGl2PageDescriptor.sharedHandle.totalLockCount -= locksToRelease
                if (webGl2PageDescriptor.sharedHandle.totalLockCount === 0) {
                    webGl2PageDescriptor.pageHandleByChannel.forEach((pageHandle) => this.context.getAtlasById(pageHandle.atlasId).unlockPage(pageHandle))
                } else if (webGl2PageDescriptor.sharedHandle.totalLockCount < 0) {
                    throw new Error("Internal error: shared lock count is negative.")
                }
            }
        })
    }

    getAllocatedPageHandles(): PageHandleInfo[] {
        return Array.from(this._pageHandleInfoByLinearPageIndex.values())
    }

    getAllocatedPageHandleForPageDescriptor(pageDescriptor: PageDescriptor): PageHandleInfo | undefined {
        const linearPageIndex = this.mipmapInfo.getLinearPageIndex(pageDescriptor)
        return this._pageHandleInfoByLinearPageIndex.get(linearPageIndex)
    }

    readonly pageTableImage: WebGl2ImagePhysical
    readonly mipmapInfo: WebGl2ImageVirtualMipmapInfo
    readonly webGl2ImageDescriptor: WebGl2ImageDescriptor

    private _pagePriority = DEFAULT_PAGE_PRIORITY
    private _pageHandleInfoByLinearPageIndex = new Map<number, PageHandleInfo>()
    private _pageTableUpdates: WebGl2ImageVirtualPageTableUpdates
    private _deferredPageLockCountByLinearPageIndex = new Map<number, number>()
    private _isFreeingPagesInternally = false
}

export type SharedHandle = {
    totalRefCount: number
    refCountByHolder: Map<WebGl2ImageVirtualPageManager, number> // number of references to this page (usually 1, but >1 if this page is shared among multiple regions or even images)
    totalLockCount: number
    lockCountByHolder: Map<WebGl2ImageVirtualPageManager, number> // number of references to this page (usually 1, but >1 if this page is shared among multiple regions or even images)
}

export type PageHandleInfo = {
    linearPageIndex: number
    pageHandleByChannel: PageHandle[]
    sharedHandle: SharedHandle
    associatedPageDescriptorsByPageHash: Map<number, PageDescriptor>
}

export type AllocatedUniquePageInfo = {
    pageHandleInfo: PageHandleInfo
    sharedSourcePageHandleInfo?: PageHandleInfo
}

const DEFAULT_PAGE_PRIORITY = 1

const logger = loggerFactory.getLogger("webgl2")
